// src/components/admin/MenuManagement.js
import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Table, Form, Modal,
  Spinner, Alert, Badge
} from 'react-bootstrap';
import {
  FaHome,
  FaUsers,
  FaUserGraduate,
  FaUniversity,
  FaUserTie,
  FaUserShield,
  FaListAlt,
  FaIdBadge,
  FaComments,
  FaCalendarPlus,
  FaCogs,
  FaClipboardList,
  FaChalkboardTeacher,FaList,FaPlus,FaEdit,FaTrash,FaEye,FaEyeSlash,FaTimes,FaSave
} from "react-icons/fa";

const iconMap = {
  Dashboard: FaHome,

  "Student List": FaUserGraduate,
  "College Management": FaUniversity,
  Profile: FaIdBadge,

  "Role Management": FaUserShield,
  "Menu Management": FaListAlt,

  "Staff Management": FaUserTie,

  Chat: FaComments,

  "Add Days": FaCalendarPlus,

  Settings: FaCogs,

  Reports: FaClipboardList,

  Teachers: FaChalkboardTeacher
};


const MenuManagement = () => {
  const [menus, setMenus] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentMenuId, setCurrentMenuId] = useState(null);

  const [formData, setFormData] = useState({
    menu_name: '',
    ui_route: '',
    is_visible: true,
    parent_menu_id: 0,
    icon: 'Home',
    display_order: 1,
    role_id: 1,
    can_view: true,
    can_edit: false,
    can_delete: false,
    college_id: null // will be set if user has college_id
  });

  // Fetch logged-in user college_id (if exists)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.college_id) {
      setFormData(prev => ({ ...prev, college_id: user.college_id }));
    }
  }, []);

  // Fetch menus & roles
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch menus
      const menuRes = await apiService.get('/api/menus');
      if (menuRes.success && menuRes.message?.menus) {
        setMenus(menuRes.message.menus);
      }

      // Fetch roles
      const roleRes = await apiService.get('/api/roles');
      if (roleRes.success && Array.isArray(roleRes.message?.roles)) {
        setRoles(roleRes.message.roles);
      }

    } catch (err) {
      setError('Error loading data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked :
              (name === 'display_order' || name === 'parent_menu_id' || name === 'role_id')
                ? Number(value)
                : value
    }));
  };

  const openModal = (menu = null) => {
    if (menu) {
      setIsEditMode(true);
      setCurrentMenuId(menu.menu_id);
      setFormData({
        menu_name: menu.menu_name || '',
        ui_route: menu.ui_route || '',
        is_visible: menu.is_visible !== false,
        parent_menu_id: menu.parent_menu_id || 0,
        icon: menu.icon || 'Home',
        display_order: menu.display_order || 1,
        role_id: menu.role_mappings?.[0]?.role_id || 1,
        can_view: menu.role_mappings?.[0]?.can_view !== false,
        can_edit: menu.role_mappings?.[0]?.can_edit || false,
        can_delete: menu.role_mappings?.[0]?.can_delete || false,
        college_id: formData.college_id // preserve college context
      });
    } else {
      setIsEditMode(false);
      setCurrentMenuId(null);
      setFormData({
        menu_name: '',
        ui_route: '',
        is_visible: true,
        parent_menu_id: 0,
        icon: 'Home',
        display_order: menus.length + 1,
        role_id: 1,
        can_view: true,
        can_edit: false,
        can_delete: false,
        college_id: formData.college_id
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.menu_name.trim() || !formData.ui_route.trim()) {
      setError('Menu name and UI route are required');
      return;
    }

    try {
      setError(null);
      setSuccessMsg('');

      const payload = {
        menu_name: formData.menu_name.trim(),
        ui_route: formData.ui_route.trim(),
        is_visible: formData.is_visible,
        parent_menu_id: Number(formData.parent_menu_id),
        icon: formData.icon,
        display_order: Number(formData.display_order),
        role_id: Number(formData.role_id),
        can_view: formData.can_view,
        can_edit: formData.can_edit,
        can_delete: formData.can_delete
      };

      // Add college_id if present (college-specific menu)
      if (formData.college_id) {
        payload.college_id = formData.college_id;
      }

      let response;
      if (isEditMode) {
        response = await apiService.put(`/api/menus/${currentMenuId}`, payload);
      } else {
        response = await apiService.post('/api/menus', payload);
      }

      if (response.success) {
        setSuccessMsg(isEditMode ? 'Menu updated successfully' : 'Menu created successfully');
        setShowModal(false);
        fetchData();
      } else {
        setError(response.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu?')) return;

    try {
      const response = await apiService.delete(`/api/menus/${id}`);
      if (response.success) {
        setSuccessMsg('Menu deleted successfully');
        fetchData();
      } else {
        setError('Failed to delete menu');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting menu');
    }
  };

  const IconPreview = ({ iconName }) => {
    const Icon = iconMap[iconName] || FaHome;
    return <Icon className="ms-2 text-primary" size={20} />;
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <FaList className="me-2 text-primary" />
            Menu Management
          </h2>
          <p className="text-muted">
            Create, edit and organize navigation menu items
            {formData.college_id && <span className="ms-2">(College-specific mode - ID: {formData.college_id})</span>}
          </p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => openModal()}>
            <FaPlus className="me-2" /> Create New Menu
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading menus...</p>
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Table responsive hover className="align-middle">
              <thead className="bg-light">
                <tr>
                  <th>#</th>
                  <th>Menu Name</th>
                  <th>Route</th>
                  <th>Icon</th>
                  <th>Order</th>
                  <th>Parent</th>
                  <th>Visible</th>
                  <th>Role</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {menus.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-5 text-muted">
                      No menu items found. Create your first menu!
                    </td>
                  </tr>
                ) : (
                  menus.map((menu, index) => (
                    <tr key={menu.menu_id}>
                      <td>{index + 1}</td>
                      <td><strong>{menu.menu_name}</strong></td>
                      <td><code>{menu.ui_route}</code></td>
                      <td>
                        <Badge bg="light" text="dark">
                          {menu.icon}
                        </Badge>
                      </td>
                      <td>{menu.display_order}</td>
                      <td>
                        {menu.parent_menu_id === 0 ? (
                          <Badge bg="info">Top Level</Badge>
                        ) : (
                          menu.parent_menu_id
                        )}
                      </td>
                      <td>
                        {menu.is_visible ? (
                          <FaEye className="text-success" />
                        ) : (
                          <FaEyeSlash className="text-muted" />
                        )}
                      </td>
                      <td>{menu.role_mappings?.[0]?.role?.role_name || '—'}</td>
                      <td>
                        <small>
                          V: {menu.role_mappings?.[0]?.can_view ? 'Yes' : 'No'} • 
                          E: {menu.role_mappings?.[0]?.can_edit ? 'Yes' : 'No'}
                        </small>
                      </td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          onClick={() => openModal(menu)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(menu.menu_id)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit Menu Item' : 'Create New Menu Item'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Menu Name *</Form.Label>
                  <Form.Control
                    name="menu_name"
                    value={formData.menu_name}
                    onChange={handleChange}
                    placeholder="e.g. Dashboard, Student List"
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>UI Route *</Form.Label>
                  <Form.Control
                    name="ui_route"
                    value={formData.ui_route}
                    onChange={handleChange}
                    placeholder="e.g. /dashboard, /students"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Icon</Form.Label>
                  <div className="d-flex align-items-center">
                    <Form.Select
                      name="icon"
                      value={formData.icon}
                      onChange={handleChange}
                      className="flex-grow-1"
                    >
                      {Object.keys(iconMap).map(icon => (
                        <option key={icon} value={icon}>{icon}</option>
                      ))}
                    </Form.Select>
                    <IconPreview iconName={formData.icon} />
                  </div>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                    min="1"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Parent Menu</Form.Label>
                  <Form.Select
                    name="parent_menu_id"
                    value={formData.parent_menu_id}
                    onChange={handleChange}
                  >
                    <option value={0}>Top Level (No Parent)</option>
                    {menus
                      .filter(m => m.menu_id !== currentMenuId) // Prevent self-parent
                      .map(menu => (
                        <option key={menu.menu_id} value={menu.menu_id}>
                          {menu.menu_name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    name="role_id"
                    value={formData.role_id}
                    onChange={handleChange}
                  >
                    {roles.map(role => (
                      <option key={role.role_id} value={role.role_id}>
                        {role.role_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />

            <h6 className="mb-3">Visibility & Permissions</h6>

            <Row>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="is_visible"
                  label="Visible in Menu"
                  checked={formData.is_visible}
                  onChange={handleChange}
                  name="is_visible"
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="can_view"
                  label="Can View"
                  checked={formData.can_view}
                  onChange={handleChange}
                  name="can_view"
                />
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="can_edit"
                  label="Can Edit"
                  checked={formData.can_edit}
                  onChange={handleChange}
                  name="can_edit"
                />
              </Col>
            </Row>

            <Form.Check
              type="switch"
              id="can_delete"
              label="Can Delete"
              checked={formData.can_delete}
              onChange={handleChange}
              name="can_delete"
              className="mt-3"
            />
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            <FaTimes className="me-2" /> Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            <FaSave className="me-2" />
            {isEditMode ? 'Update Menu' : 'Create Menu'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MenuManagement;